import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { NAV_SECTIONS } from '@/app/shared/nav/nav-sections.token';
import { NavItem, NavSection } from '@/app/shared/nav/nav-section.model';

@Injectable({ providedIn: 'root' })
export class ShellStore {
  private readonly config  = inject(APP_CONFIG);
  private readonly router  = inject(Router);
  private readonly doc     = inject(DOCUMENT);

  private readonly rawSections =
    (inject(NAV_SECTIONS, { optional: true }) as NavSection[] | null) ?? [];

  readonly sidebarCollapsed = signal(false);
  readonly drilledItem      = signal<NavItem | null>(null);
  readonly animDir          = signal<'in' | 'out' | 'none'>('none');

  readonly activeUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects),
    ),
    { initialValue: this.doc.location.pathname },
  );

  readonly sections = computed<NavSection[]>(() => this.rawSections);
  readonly envLabel = this.config.environment;

  constructor() {
    effect(() => this._syncFromUrl(this.activeUrl() ?? ''));
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update(v => !v);
  }

  drillIn(item: NavItem): void {
    if (!item.children?.length) {
      if (item.route) this.router.navigate([item.route]);
      return;
    }
    this.animDir.set('in');
    this.drilledItem.set(item);
    const leaf = this._firstLeaf(item.children);
    if (leaf?.route) this.router.navigate([leaf.route]);
  }

  drillOut(): void {
    this.animDir.set('out');
    this.drilledItem.set(null);
  }

  isItemActive(item: NavItem): boolean {
    const url = this.activeUrl() ?? '';
    if (item.route) {
      return item.route === '/'
        ? url === '/'
        : url === item.route || url.startsWith(item.route + '/');
    }
    return item.children ? this._urlMatchesAnyDescendant(item.children, url) : false;
  }

  private _syncFromUrl(url: string): void {
    for (const section of this.rawSections) {
      for (const item of section.items) {
        if (item.children?.length && this._urlMatchesAnyDescendant(item.children, url)) {
          this.drilledItem.set(item);
          return;
        }
      }
    }
    this.drilledItem.set(null);
  }

  private _urlMatchesAnyDescendant(items: NavItem[], url: string): boolean {
    return items.some(item => {
      if (item.route) {
        return item.route === '/'
          ? url === '/'
          : url === item.route || url.startsWith(item.route + '/');
      }
      return item.children ? this._urlMatchesAnyDescendant(item.children, url) : false;
    });
  }

  private _firstLeaf(items: NavItem[]): NavItem | null {
    for (const item of items) {
      if (item.route) return item;
      if (item.children) {
        const found = this._firstLeaf(item.children);
        if (found) return found;
      }
    }
    return null;
  }
}
